import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { doc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import { useFormConfig } from "../hooks/useFormConfig";

const FIELD_TYPES = [
    { value: "text", label: "Tekst" },
    { value: "email", label: "E-mail" },
    { value: "tel", label: "Telefon" },
    { value: "number", label: "Numer" },
    { value: "checkbox", label: "Checkboxy" },
    { value: "radio", label: "Radio" },
];

export default function FormConfigEditor() {
    const { formId } = useParams();
    const { config, loading } = useFormConfig(formId);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (config?.title) {
            document.title = `${config.title} | Konfiguracja`;
        }
        return () => { document.title = "Salezjanie - formularze"; };
    }, [config?.title]);
    const [saveMsg, setSaveMsg] = useState(null);
    const [editFields, setEditFields] = useState(null);
    const [editTitle, setEditTitle] = useState("");
    const [editSubtitle, setEditSubtitle] = useState("");
    const [editEmailEnabled, setEditEmailEnabled] = useState(false);
    const [editEmailSubject, setEditEmailSubject] = useState("");
    const [editEmailMessage, setEditEmailMessage] = useState("");
    const [editEmailImageUrls, setEditEmailImageUrls] = useState([]);
    const [uploadingEmailImage, setUploadingEmailImage] = useState(false);
    const [editDeadline, setEditDeadline] = useState("");
    const [editStartTime, setEditStartTime] = useState("");

    const handleEmailImageUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        setUploadingEmailImage(true);
        try {
            const newUrls = [];
            for (const file of files) {
                const fileRef = ref(storage, `email-images/${formId}/${Date.now()}_${file.name}`);
                await uploadBytes(fileRef, file);
                const url = await getDownloadURL(fileRef);
                newUrls.push(url);
            }
            setEditEmailImageUrls((prev) => [...prev, ...newUrls]);
        } catch (err) {
            alert(`Błąd wgrywania obrazka: ${err.message}`);
        } finally {
            setUploadingEmailImage(false);
            e.target.value = "";
        }
    };

    // Initialize local state when config loads
    const fields =
        editFields !== null ? editFields : config?.fields || [];
    const title = editFields !== null ? editTitle : config?.title || "";
    const subtitle =
        editFields !== null ? editSubtitle : config?.subtitle || "";

    const startEditing = () => {
        setEditFields([...(config?.fields || [])]);
        setEditTitle(config?.title || "");
        setEditSubtitle(config?.subtitle || "");
        setEditEmailEnabled(config?.emailEnabled || false);
        setEditEmailSubject(config?.emailSubject || "Potwierdzenie rejestracji");
        setEditEmailMessage(config?.emailMessage || "");
        setEditEmailImageUrls(config?.emailImageUrls || []);
        setEditDeadline(config?.deadline || "");
        setEditStartTime(config?.startTime || "");
        setSaveMsg(null);
    };

    const cancelEditing = () => {
        setEditFields(null);
        setSaveMsg(null);
    };

    const isEditing = editFields !== null;

    const updateField = (index, key, value) => {
        const updated = [...editFields];
        if (key === "required" || key === "halfWidth") {
            updated[index] = { ...updated[index], [key]: value === "true" };
        } else if (key === "type") {
            // checkbox and radio both need an options array
            const needsOptions = value === "checkbox" || value === "radio";
            const newField = { ...updated[index], [key]: value };
            if (needsOptions) {
                newField.options = newField.options || [""];
            } else {
                delete newField.options;
            }
            updated[index] = newField;
        } else {
            updated[index] = { ...updated[index], [key]: value };
        }
        setEditFields(updated);
    };

    const addCheckboxOption = (fieldIndex) => {
        const updated = [...editFields];
        const opts = [...(updated[fieldIndex].options || []), ""];
        updated[fieldIndex] = { ...updated[fieldIndex], options: opts };
        setEditFields(updated);
    };

    const updateCheckboxOption = (fieldIndex, optIndex, value) => {
        const updated = [...editFields];
        const opts = [...(updated[fieldIndex].options || [])];
        opts[optIndex] = value;
        updated[fieldIndex] = { ...updated[fieldIndex], options: opts };
        setEditFields(updated);
    };

    const removeCheckboxOption = (fieldIndex, optIndex) => {
        const updated = [...editFields];
        const opts = updated[fieldIndex].options.filter((_, i) => i !== optIndex);
        updated[fieldIndex] = { ...updated[fieldIndex], options: opts };
        setEditFields(updated);
    };

    const addField = () => {
        setEditFields([
            ...editFields,
            {
                id: `field_${Date.now()}`,
                label: "",
                type: "text",
                placeholder: "",
                required: false,
                halfWidth: false,
            },
        ]);
    };

    const handleSave_checkboxValidation = (fields) => {
        for (const f of fields) {
            if (f.type === "checkbox" || f.type === "radio") {
                const validOpts = (f.options || []).filter((o) => o.trim());
                if (validOpts.length === 0) return "Pole wyboru musi mieć co najmniej jedną opcję.";
            }
        }
        return null;
    };

    const removeField = (index) => {
        setEditFields(editFields.filter((_, i) => i !== index));
    };

    const moveField = (index, direction) => {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= editFields.length) return;
        const updated = [...editFields];
        [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
        setEditFields(updated);
    };

    const handleSave = async () => {
        // Validate
        for (const field of editFields) {
            if (!field.id.trim() || !field.label.trim()) {
                setSaveMsg({ type: "error", text: "Każde pole musi mieć ID i etykietę." });
                return;
            }
        }

        const ids = editFields.map((f) => f.id);
        if (new Set(ids).size !== ids.length) {
            setSaveMsg({ type: "error", text: "ID pól muszą być unikalne." });
            return;
        }

        const checkboxErr = handleSave_checkboxValidation(editFields);
        if (checkboxErr) {
            setSaveMsg({ type: "error", text: checkboxErr });
            return;
        }

        // Clean fields before saving
        const cleanedFields = editFields.map((f) => {
            const newF = { ...f };
            if (newF.type === "checkbox" || newF.type === "radio") {
                newF.options = (newF.options || []).filter((o) => o.trim());
            } else {
                delete newF.options;
            }
            // Strip any explicit undefined values to prevent Firestore errors
            Object.keys(newF).forEach(k => {
                if (newF[k] === undefined) {
                    delete newF[k];
                }
            });
            return newF;
        });

        setSaving(true);
        setSaveMsg(null);

        try {
            await updateDoc(doc(db, "forms", formId), {
                title: editTitle,
                subtitle: editSubtitle,
                fields: cleanedFields,
                emailEnabled: editEmailEnabled,
                emailSubject: editEmailSubject,
                emailMessage: editEmailMessage,
                emailImageUrls: editEmailImageUrls,
                deadline: editDeadline || null,
                startTime: editStartTime || null,
            });
            setSaveMsg({ type: "success", text: "Zapisano!" });
            setEditFields(null);
        } catch (err) {
            setSaveMsg({
                type: "error",
                text: `Błąd zapisu: ${err.message}`,
            });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="config-section">
                <p className="config-loading">Ładowanie konfiguracji...</p>
            </div>
        );
    }

    return (
        <div className="config-section">
            <div className="config-header">
                <h2 className="summary-title">Konfiguracja formularza</h2>
                {!isEditing ? (
                    <button className="btn-small btn-edit" onClick={startEditing}>
                        ✏️ Edytuj
                    </button>
                ) : (
                    <div className="config-actions">
                        <button
                            className="btn-small btn-save"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? "Zapisywanie..." : "💾 Zapisz"}
                        </button>
                        <button className="btn-small btn-cancel" onClick={cancelEditing}>
                            Anuluj
                        </button>
                    </div>
                )}
            </div>

            {saveMsg && (
                <div
                    className={`config-msg ${saveMsg.type === "error" ? "config-msg-error" : "config-msg-success"
                        }`}
                >
                    {saveMsg.text}
                </div>
            )}

            {/* Title & Subtitle */}
            <div className="config-meta">
                <div className="config-meta-field">
                    <label>Tytuł formularza</label>
                    {isEditing ? (
                        <input
                            type="text"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                        />
                    ) : (
                        <span>{title}</span>
                    )}
                </div>
                <div className="config-meta-field">
                    <label>Podtytuł</label>
                    {isEditing ? (
                        <input
                            type="text"
                            value={editSubtitle}
                            onChange={(e) => setEditSubtitle(e.target.value)}
                        />
                    ) : (
                        <span>{subtitle}</span>
                    )}
                </div>
                <div className="config-meta-field">
                    <label>
                        Otwarcie zgłoszeń
                        <span style={{ fontWeight: 400, color: '#8a8580', fontSize: '0.8rem', marginLeft: '0.5rem' }}>
                            (formularz niedostępny przed tą datą)
                        </span>
                    </label>
                    {isEditing ? (
                        <input
                            type="datetime-local"
                            value={editStartTime}
                            onChange={(e) => setEditStartTime(e.target.value)}
                        />
                    ) : (
                        <span>
                            {config?.startTime
                                ? new Date(config.startTime).toLocaleString("pl-PL")
                                : <span style={{ color: '#8a8580', fontSize: '0.85rem' }}>Brak</span>}
                        </span>
                    )}
                </div>
                <div className="config-meta-field">
                    <label>
                        Termin zgłoszeń
                        <span style={{ fontWeight: 400, color: '#8a8580', fontSize: '0.8rem', marginLeft: '0.5rem' }}>
                            (ostatni moment na wysłanie formularza)
                        </span>
                    </label>
                    {isEditing ? (
                        <input
                            type="datetime-local"
                            value={editDeadline}
                            onChange={(e) => setEditDeadline(e.target.value)}
                        />
                    ) : (
                        <span>
                            {config?.deadline
                                ? new Date(config.deadline).toLocaleString("pl-PL")
                                : <span style={{ color: '#8a8580', fontSize: '0.85rem' }}>Brak</span>}
                        </span>
                    )}
                </div>
            </div>

            {/* Email confirmation config */}
            <div className="config-meta" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #f0ebe6' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                    <span style={{ fontSize: '1rem' }}>✉️</span>
                    <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#5a4a3a' }}>E-mail potwierdzający</span>
                </div>
                <div className="config-meta-field">
                    <label>Wysyłaj e-mail po rejestracji</label>
                    {isEditing ? (
                        <select
                            value={String(editEmailEnabled)}
                            onChange={(e) => setEditEmailEnabled(e.target.value === "true")}
                            className="input-sm"
                        >
                            <option value="true">✅ Tak</option>
                            <option value="false">❌ Nie</option>
                        </select>
                    ) : (
                        <span>{config?.emailEnabled ? "✅ Tak" : "❌ Nie"}</span>
                    )}
                </div>

                {(isEditing || config?.emailEnabled) && (
                    <>
                        <div className="config-meta-field">
                            <label>Temat e-maila</label>
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={editEmailSubject}
                                    onChange={(e) => setEditEmailSubject(e.target.value)}
                                    placeholder="np. Potwierdzenie rejestracji"
                                />
                            ) : (
                                <span>{config?.emailSubject}</span>
                            )}
                        </div>
                        <div className="config-meta-field" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '0.4rem' }}>
                            <label style={{ marginBottom: 0 }}>
                                Treść e-maila
                                <span style={{ fontWeight: 400, color: '#8a8580', fontSize: '0.8rem', marginLeft: '0.5rem' }}>
                                    (użyj <code>{'{{imie}}'}</code> aby wstawić imię uczestnika)
                                </span>
                            </label>
                            {isEditing ? (
                                <textarea
                                    value={editEmailMessage}
                                    onChange={(e) => setEditEmailMessage(e.target.value)}
                                    rows={8}
                                    style={{
                                        width: '100%',
                                        maxWidth: '500px',
                                        fontFamily: 'inherit',
                                        fontSize: '0.9rem',
                                        padding: '0.5rem',
                                        borderRadius: '8px',
                                        border: '1.5px solid #e5e0db',
                                        resize: 'vertical',
                                        lineHeight: '1.5',
                                    }}
                                    placeholder={`Drogi/a {{imie}},\n\nDziękujemy za rejestrację!\n...`}
                                />
                            ) : (
                                <pre style={{
                                    whiteSpace: 'pre-wrap',
                                    fontFamily: 'inherit',
                                    fontSize: '0.85rem',
                                    color: '#8a8580',
                                    background: '#faf8f6',
                                    padding: '0.75rem',
                                    borderRadius: '8px',
                                    border: '1px solid #e5e0db',
                                    maxWidth: '500px',
                                    margin: 0,
                                }}>{config?.emailMessage}</pre>
                            )}
                        </div>
                        {/* Email images */}
                        <div className="config-meta-field" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '0.5rem' }}>
                            <label style={{ marginBottom: 0 }}>Obrazki w e-mailu</label>
                            {isEditing ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', width: '100%', maxWidth: '500px' }}>
                                    {/* Thumbnails of uploaded images */}
                                    {editEmailImageUrls.length > 0 && (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                            {editEmailImageUrls.map((url, i) => (
                                                <div key={i} style={{ position: 'relative', display: 'inline-block' }}>
                                                    <img
                                                        src={url}
                                                        alt={`email img ${i}`}
                                                        style={{ height: '72px', width: '72px', objectFit: 'cover', borderRadius: '6px', display: 'block', border: '1.5px solid #e5e0db' }}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setEditEmailImageUrls(editEmailImageUrls.filter((_, j) => j !== i))}
                                                        style={{
                                                            position: 'absolute', top: '-6px', right: '-6px',
                                                            background: '#e53935', color: 'white', border: 'none',
                                                            borderRadius: '50%', width: '18px', height: '18px',
                                                            fontSize: '11px', cursor: 'pointer', lineHeight: '18px',
                                                            padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center'
                                                        }}
                                                    >✕</button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {/* File upload button */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <label
                                            style={{
                                                display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                                                padding: '0.3rem 0.75rem', borderRadius: '6px',
                                                border: '1.5px solid #c8bfb8', background: '#faf8f6',
                                                fontSize: '0.85rem', cursor: uploadingEmailImage ? 'not-allowed' : 'pointer',
                                                opacity: uploadingEmailImage ? 0.6 : 1, color: '#5a4a3a', fontWeight: 500,
                                            }}
                                        >
                                            {uploadingEmailImage ? '⏳ Wgrywanie...' : '🖼️ Dodaj obrazek'}
                                            <input
                                                type="file"
                                                accept="image/*"
                                                multiple
                                                disabled={uploadingEmailImage}
                                                onChange={handleEmailImageUpload}
                                                style={{ display: 'none' }}
                                            />
                                        </label>
                                    </div>
                                </div>
                            ) : (
                                (config?.emailImageUrls || []).length > 0 ? (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                        {config.emailImageUrls.map((url, i) => (
                                            <img key={i} src={url} alt={`email img ${i}`} style={{ height: '48px', borderRadius: '4px', objectFit: 'cover' }} />
                                        ))}
                                    </div>
                                ) : <span style={{ color: '#8a8580', fontSize: '0.85rem' }}>Brak obrazków</span>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* Fields */}
            <div className="config-fields-list">
                <p className="config-fields-label">Pola formularza ({fields.length})</p>
                {fields.map((field, index) => (
                    <div key={field.id + index} className="config-field-row">
                        {isEditing ? (
                            <>
                                <div className="config-field-inputs">
                                    <div className="config-input-group">
                                        <label>ID</label>
                                        <input
                                            type="text"
                                            value={field.id}
                                            onChange={(e) =>
                                                updateField(index, "id", e.target.value)
                                            }
                                            className="input-sm"
                                        />
                                    </div>
                                    <div className="config-input-group">
                                        <label>Etykieta</label>
                                        <input
                                            type="text"
                                            value={field.label}
                                            onChange={(e) =>
                                                updateField(index, "label", e.target.value)
                                            }
                                            className="input-sm"
                                        />
                                    </div>
                                    <div className="config-input-group">
                                        <label>Typ</label>
                                        <select
                                            value={field.type}
                                            onChange={(e) =>
                                                updateField(index, "type", e.target.value)
                                            }
                                            className="input-sm"
                                        >
                                            {FIELD_TYPES.map((t) => (
                                                <option key={t.value} value={t.value}>
                                                    {t.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="config-input-group">
                                        <label>Placeholder</label>
                                        <input
                                            type="text"
                                            value={field.placeholder || ""}
                                            onChange={(e) =>
                                                updateField(index, "placeholder", e.target.value)
                                            }
                                            className="input-sm"
                                        />
                                    </div>
                                    <div className="config-input-group config-input-narrow">
                                        <label>Wymagane</label>
                                        <select
                                            value={String(field.required)}
                                            onChange={(e) =>
                                                updateField(index, "required", e.target.value)
                                            }
                                            className="input-sm"
                                        >
                                            <option value="true">Tak</option>
                                            <option value="false">Nie</option>
                                        </select>
                                    </div>
                                    <div className="config-input-group config-input-narrow">
                                        <label>Pół szer.</label>
                                        <select
                                            value={String(field.halfWidth)}
                                            onChange={(e) =>
                                                updateField(index, "halfWidth", e.target.value)
                                            }
                                            className="input-sm"
                                        >
                                            <option value="true">Tak</option>
                                            <option value="false">Nie</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Checkbox / Radio options editor */}
                                {(field.type === "checkbox" || field.type === "radio") && (
                                    <div className="checkbox-options-editor">
                                        <p className="checkbox-options-label">
                                            {field.type === "radio" ? "Opcje radio" : "Opcje checkboxów"}
                                        </p>
                                        {(field.options || []).map((opt, oi) => (
                                            <div key={oi} className="checkbox-option-row">
                                                <input
                                                    type="text"
                                                    className="input-sm"
                                                    placeholder={`Opcja ${oi + 1}`}
                                                    value={opt}
                                                    onChange={(e) =>
                                                        updateCheckboxOption(index, oi, e.target.value)
                                                    }
                                                />
                                                <button
                                                    className="btn-icon btn-icon-danger"
                                                    onClick={() => removeCheckboxOption(index, oi)}
                                                    title="Usuń opcję"
                                                    disabled={(field.options || []).length <= 1}
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            className="btn-small btn-add-field"
                                            style={{ marginTop: "0.35rem" }}
                                            onClick={() => addCheckboxOption(index)}
                                        >
                                            + Dodaj opcję
                                        </button>
                                    </div>
                                )}

                                <div className="config-field-actions">
                                    <button
                                        className="btn-icon"
                                        onClick={() => moveField(index, -1)}
                                        disabled={index === 0}
                                        title="W górę"
                                    >
                                        ↑
                                    </button>
                                    <button
                                        className="btn-icon"
                                        onClick={() => moveField(index, 1)}
                                        disabled={index === fields.length - 1}
                                        title="W dół"
                                    >
                                        ↓
                                    </button>
                                    <button
                                        className="btn-icon btn-icon-danger"
                                        onClick={() => removeField(index)}
                                        title="Usuń"
                                    >
                                        ✕
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="config-field-display">
                                <span className="field-label">{field.label}</span>
                                <span className="field-meta">
                                    {field.type}
                                    {(field.type === "checkbox" || field.type === "radio") && field.options?.length
                                        ? ` (${field.options.length} opcje)`
                                        : ""}
                                    {" · "}{field.id}
                                    {field.required && " · wymagane"}
                                    {field.halfWidth && " · pół szer."}
                                </span>
                            </div>
                        )}
                    </div>
                ))}

                {isEditing && (
                    <button className="btn-small btn-add-field" onClick={addField}>
                        + Dodaj pole
                    </button>
                )}
            </div>
        </div>
    );
}
